import React, { useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { ComposableMap, Geographies, Geography } from 'react-simple-maps';
import axios from 'axios';
import 'iconify-icon';
import brazilGeo from '../data/brazil-states.json';
import {
  STATES_LIST,
  STATE_CENTROIDS,
  resolveState,
  haversineKm,
} from '../data/brazil-state-centroids';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const normalize = (s) =>
  (s || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

/**
 * FranchiseLocator
 * Dark, cinematic locator for Instituto Eneagrama franquias.
 * - Country > State > City cascading dropdowns
 * - Interactive SVG map: states with active franquias glow #FF6700
 * - Returns the 3 nearest franquias based on selected state/city centroid
 *
 * Props:
 *  - compact: boolean → renders a denser version for embedding on the Home page.
 */
const FranchiseLocator = ({ compact = false, hideHeader = false }) => {
  const [franchises, setFranchises] = useState([]);
  const [country, setCountry] = useState('BR');
  const [stateCode, setStateCode] = useState('');
  const [city, setCity] = useState('');
  const [hoverState, setHoverState] = useState(null);
  const [loading, setLoading] = useState(true);

  // Read URL params so deep-linking from the home "Ver todas" preserves the filter.
  // Only active on non-compact mode (i.e. the dedicated /franquias page).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (compact) return;
    const s = searchParams.get('state');
    const c = searchParams.get('city');
    if (s) setStateCode(s.toUpperCase());
    if (c) setCity(c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compact]);

  // Keep the URL in sync with the current filters (non-compact only).
  useEffect(() => {
    if (compact) return;
    const next = new URLSearchParams(searchParams);
    if (stateCode) next.set('state', stateCode);
    else next.delete('state');
    if (city) next.set('city', city);
    else next.delete('city');
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateCode, city, compact]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/public/franchises`);
        setFranchises(Array.isArray(data) ? data : []);
      } catch {
        setFranchises([]);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Enrich franchises with their resolved state {code, name, lat, lng}
  const enriched = useMemo(
    () =>
      franchises.map((f) => {
        const st = resolveState(f.state);
        return { ...f, _state: st };
      }),
    [franchises],
  );

  // Set of state codes/names that actually have franquias
  const statesWithFranchises = useMemo(() => {
    const codes = new Set();
    const names = new Set();
    enriched.forEach((f) => {
      if (f._state) {
        codes.add(f._state.code);
        names.add(normalize(f._state.name));
      }
    });
    return { codes, names };
  }, [enriched]);

  // Cities available for the currently-selected state (drawn from cities_served + city)
  const citiesForState = useMemo(() => {
    if (!stateCode) return [];
    const inState = enriched.filter((f) => f._state?.code === stateCode);
    const set = new Set();
    inState.forEach((f) => {
      if (f.city) set.add(f.city.trim());
      (f.cities_served || []).forEach((c) => c && set.add(String(c).trim()));
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [enriched, stateCode]);

  // Nearest franquias: when no filters, returns up to 6 random; when state selected,
  // orders by distance to that state centroid (or franchise's own lat/lng when present),
  // prioritizing same-state matches and city matches.
  const nearestFranchises = useMemo(() => {
    if (!enriched.length) return [];
    if (!stateCode) return enriched.slice(0, 3);
    const target = STATE_CENTROIDS[stateCode];
    if (!target) return enriched.slice(0, 3);
    const scored = enriched.map((f) => {
      // Prefer franchise-level lat/lng (set in seed) over state centroid
      const point =
        typeof f.latitude === 'number' && typeof f.longitude === 'number'
          ? { lat: f.latitude, lng: f.longitude }
          : f._state;
      const dist = point ? haversineKm(target, point) : Infinity;
      const sameState = f._state?.code === stateCode ? -1 : 0;
      const matchesCity =
        city &&
        (normalize(f.city) === normalize(city) ||
          (f.cities_served || []).some((c) => normalize(c) === normalize(city)));
      const cityBonus = matchesCity ? -10000 : 0;
      return { f, score: dist + sameState * 0.001 + cityBonus, dist };
    });
    scored.sort((a, b) => a.score - b.score);
    return scored.slice(0, 3).map((x) => ({ ...x.f, _distanceKm: x.dist }));
  }, [enriched, stateCode, city]);

  const selectedStateMeta = stateCode ? { code: stateCode, ...STATE_CENTROIDS[stateCode] } : null;

  const handleClearFilters = () => {
    setStateCode('');
    setCity('');
  };

  return (
    <section
      id="franchises"
      className="relative border-t border-neutral-900"
      style={{ background: 'rgba(10, 10, 10, 0.78)' }}
      data-testid="franchise-locator-section"
    >
      <div className={`relative z-10 max-w-6xl mx-auto px-6 md:px-12 ${compact ? 'py-16' : 'py-24'}`}>
        {/* HEADER */}
        {!hideHeader && (
          <div className="text-center mb-12">
            <span className="text-xs font-mono uppercase tracking-[0.3em] text-accent-orange mb-3 block">
              Rede IE Eneagrama
            </span>
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-[#EAEAEA] mb-4">
              Encontre o IE mais perto de você
            </h2>
            <p className="text-base md:text-lg text-neutral-400 max-w-2xl mx-auto leading-[1.7]">
              Filtre por país, estado e cidade. As 3 franquias mais próximas aparecem ao lado do mapa.
            </p>
          </div>
        )}

        {/* FILTERS */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-10 max-w-4xl mx-auto">
          <SelectField
            testId="locator-country"
            label="País"
            value={country}
            onChange={setCountry}
            options={[{ value: 'BR', label: 'Brasil' }]}
          />
          <SelectField
            testId="locator-state"
            label="Estado"
            value={stateCode}
            onChange={(v) => {
              setStateCode(v);
              setCity('');
            }}
            options={[
              { value: '', label: 'Todos os estados' },
              ...STATES_LIST.map((s) => ({
                value: s.code,
                label: s.name + (statesWithFranchises.codes.has(s.code) ? ' ●' : ''),
              })),
            ]}
          />
          <SelectField
            testId="locator-city"
            label="Cidade"
            value={city}
            onChange={setCity}
            disabled={!stateCode || citiesForState.length === 0}
            options={[
              { value: '', label: stateCode ? 'Todas as cidades' : 'Selecione um estado' },
              ...citiesForState.map((c) => ({ value: c, label: c })),
            ]}
          />
        </div>

        {/* MAP + RESULTS GRID */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* MAP */}
          <div
            className="lg:col-span-3 relative bg-[#0A0A0A] rounded-2xl overflow-hidden border border-[#1F1F1F]"
            data-testid="franchise-map-container"
          >
            <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_30%,rgba(255,103,0,0.08),transparent_60%)]"></div>

            <ComposableMap
              projection="geoMercator"
              projectionConfig={{ scale: 800, center: [-54, -15] }}
              width={800}
              height={780}
              style={{ width: '100%', height: 'auto', position: 'relative', zIndex: 1 }}
            >
              <Geographies geography={brazilGeo}>
                {({ geographies }) =>
                  geographies.map((geo) => {
                    const name = geo.properties.name;
                    const sigla = geo.properties.sigla;
                    const hasFranchise = statesWithFranchises.names.has(normalize(name));
                    const isSelected = stateCode && STATE_CENTROIDS[stateCode]?.name === name;
                    const baseFill = isSelected
                      ? '#FF6700'
                      : hasFranchise
                      ? '#4d2d12'
                      : '#1A1A1A';
                    const hoverFill = isSelected
                      ? '#FF6700'
                      : hasFranchise
                      ? '#FF6700'
                      : '#262626';
                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        onMouseEnter={() => setHoverState(name)}
                        onMouseLeave={() => setHoverState(null)}
                        onClick={() => {
                          if (!hasFranchise) return;
                          // Map state name → code
                          const entry = Object.entries(STATE_CENTROIDS).find(
                            ([, v]) => v.name === name,
                          );
                          if (entry) {
                            setStateCode(entry[0]);
                            setCity('');
                          }
                        }}
                        data-testid={`map-state-${sigla}`}
                        style={{
                          default: {
                            fill: baseFill,
                            stroke: '#000000',
                            strokeWidth: 0.6,
                            outline: 'none',
                            cursor: hasFranchise ? 'pointer' : 'default',
                            transition: 'fill 280ms cubic-bezier(0.16, 1, 0.3, 1)',
                          },
                          hover: {
                            fill: hoverFill,
                            stroke: '#000000',
                            strokeWidth: 0.75,
                            outline: 'none',
                            cursor: hasFranchise ? 'pointer' : 'default',
                          },
                          pressed: {
                            fill: '#FF6700',
                            stroke: '#000000',
                            outline: 'none',
                          },
                        }}
                      />
                    );
                  })
                }
              </Geographies>
            </ComposableMap>

            {/* Hover tooltip */}
            {hoverState && (
              <div className="pointer-events-none absolute top-4 left-4 bg-[#0A0A0A]/90 backdrop-blur-md border border-[#333333] text-[#EAEAEA] px-4 py-2 rounded-full text-sm font-medium z-10">
                {hoverState}
                {statesWithFranchises.names.has(normalize(hoverState)) ? (
                  <span className="ml-2 px-2 py-0.5 bg-accent-orange text-white rounded-full text-xs">
                    com franquia
                  </span>
                ) : (
                  <span className="ml-2 text-xs text-neutral-500">sem franquia</span>
                )}
              </div>
            )}

            {/* Legend */}
            <div className="absolute bottom-4 left-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-neutral-400 z-10">
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm bg-[#4d2d12] border border-[#FF6700]/40"></span>
                <span>Com franquia</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm bg-accent-orange"></span>
                <span>Selecionado</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm bg-[#1A1A1A] border border-[#333333]"></span>
                <span>Sem franquia</span>
              </div>
            </div>
          </div>

          {/* RESULTS */}
          <div className="lg:col-span-2 space-y-4">
            <div className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-2xl p-6">
              <div className="flex items-center justify-between mb-5">
                <div className="min-w-0">
                  <div className="text-xs font-mono uppercase tracking-widest text-accent-orange">
                    {selectedStateMeta ? '3 mais próximas' : 'Destaques'}
                  </div>
                  <div className="text-lg font-bold text-[#EAEAEA] truncate">
                    {selectedStateMeta?.name || 'Todas as regiões'}
                    {city && <span className="text-neutral-500 text-sm font-normal"> · {city}</span>}
                  </div>
                </div>
                {(stateCode || city) && (
                  <button
                    onClick={handleClearFilters}
                    data-testid="locator-clear-filters"
                    className="text-xs font-mono uppercase tracking-widest text-neutral-500 hover:text-accent-orange transition-colors flex items-center gap-1"
                  >
                    Limpar
                    <iconify-icon icon="solar:close-circle-linear"></iconify-icon>
                  </button>
                )}
              </div>

              {loading ? (
                <div className="text-sm text-neutral-500 py-8 text-center">Carregando franquias…</div>
              ) : nearestFranchises.length === 0 ? (
                <div className="text-sm text-neutral-500 py-8 text-center">
                  Nenhuma franquia disponível no momento.
                </div>
              ) : (
                <ul className="space-y-3" data-testid="locator-results">
                  {nearestFranchises.map((f) => (
                    <li key={f.franchise_id}>
                      <Link
                        to={`/franquia/${f.franchise_id}`}
                        data-testid={`locator-card-${f.franchise_id}`}
                        className="block bg-[#1A1A1A] border border-[#333333] rounded-xl p-4 hover:border-accent-orange transition-colors group"
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-full bg-[#1F1F1F] border border-[#333333] flex items-center justify-center flex-shrink-0 text-accent-orange">
                            <iconify-icon icon="solar:map-point-bold" className="text-xl"></iconify-icon>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="font-semibold text-[#EAEAEA] truncate group-hover:text-accent-orange transition-colors">
                              {f.name || f.city}
                            </div>
                            <div className="text-xs text-neutral-500 truncate">
                              {f._state?.name || f.state}
                              {f.city && ` · ${f.city}`}
                            </div>
                            {f._distanceKm !== undefined && f._distanceKm !== Infinity && f._state?.code !== stateCode && (
                              <div className="text-[10px] font-mono uppercase tracking-widest text-accent-orange mt-1">
                                ~{Math.round(f._distanceKm)} km
                              </div>
                            )}
                          </div>
                          <iconify-icon
                            icon="solar:arrow-right-linear"
                            className="text-xl text-neutral-500 group-hover:text-accent-orange transition-colors flex-shrink-0"
                          ></iconify-icon>
                        </div>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}

              <Link
                to={stateCode ? `/franquias?state=${stateCode}&expanded=1` : '/franquias?expanded=1'}
                data-testid="locator-view-all"
                className="mt-5 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-accent-orange hover:text-[#E55D00]"
              >
                Ver todas as franquias
                <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
              </Link>
            </div>

            {!compact && (
              <div className="bg-[#0F0F0F] border border-[#1F1F1F] rounded-2xl p-6">
                <div className="text-xs font-mono uppercase tracking-widest text-accent-orange mb-2">
                  Quer levar o IE para sua região?
                </div>
                <p className="text-sm text-neutral-400 mb-4">
                  Conheça o modelo de franquia do Instituto Eneagrama e candidate-se.
                </p>
                <Link
                  to="/franqueado"
                  data-testid="locator-become-franchisee"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-accent-orange text-white rounded-full text-xs font-bold uppercase tracking-wider hover:bg-[#E55D00] transition-colors"
                >
                  Seja um franqueado
                  <iconify-icon icon="solar:arrow-right-linear"></iconify-icon>
                </Link>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Compact dark-styled select
const SelectField = ({ label, value, onChange, options, testId, disabled = false }) => (
  <label className="block">
    <span className="text-[10px] font-mono uppercase tracking-[0.25em] text-neutral-500 mb-1.5 block">
      {label}
    </span>
    <div className="relative">
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        data-testid={testId}
        className={`w-full appearance-none bg-[#1A1A1A] border border-[#333333] text-[#EAEAEA] rounded-xl px-4 py-3 pr-10 text-sm focus:outline-none focus:border-accent-orange transition-colors ${
          disabled ? 'opacity-50 cursor-not-allowed' : 'hover:border-accent-orange'
        }`}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value} className="bg-[#1A1A1A] text-[#EAEAEA]">
            {o.label}
          </option>
        ))}
      </select>
      <iconify-icon
        icon="solar:alt-arrow-down-linear"
        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-500 pointer-events-none"
      ></iconify-icon>
    </div>
  </label>
);

export default FranchiseLocator;
